import React, { useEffect, useState } from 'react';

const TOUR_KEY = 'sf:editorTourSeen';

const STEPS = [
    {
        sectionId: 'appearance',
        title: 'Pick your colours',
        body: 'Card sets the background, Accent sets buttons. Defaults are fine - change them when you like.',
    },
    {
        sectionId: 'content',
        title: 'Set the start screen',
        body: 'Your title and a one-line how-to. This is what players see when they open the game.',
    },
    {
        sectionId: 'gameSetup',
        title: 'Add your logo',
        body: 'Upload the image players collect in the game - your branded item.',
    },
    {
        sectionId: null,
        title: 'Save & preview',
        body: 'Save (top right) keeps your changes. Preview to test it on your phone. Publish from the campaign page when ready.',
    },
];

function hasSeenTour() {
    if (typeof window === 'undefined') return true;
    try { return Boolean(window.localStorage.getItem(TOUR_KEY)); }
    catch { return true; }
}

function markTourSeen() {
    if (typeof window === 'undefined') return;
    try { window.localStorage.setItem(TOUR_KEY, '1'); } catch { /* private mode */ }
}

/**
 * Desktop-only first-run coach-mark. Mirrors the mobile wizard's three
 * concepts (colours / start screen / logo) plus a save+preview reminder,
 * but lives inside the editor where a wide canvas already exists. Triggers
 * once per device when a user opens an unedited draft.
 */
export default function EditorTour({ campaign, activeSection, setActiveSection }) {
    const [stepIndex, setStepIndex] = useState(0);
    const [visible, setVisible] = useState(false);

    useEffect(() => {
        if (!campaign) return;
        if (campaign.editedInEditor === true) return;
        if (hasSeenTour()) return;
        if (typeof window === 'undefined') return;
        // Only show on desktop - mobile first-timers go through the wizard.
        if (window.innerWidth < 768) return;
        setVisible(true);
    }, [campaign]);

    useEffect(() => {
        if (!visible) return;
        const step = STEPS[stepIndex];
        if (step?.sectionId && setActiveSection && activeSection !== step.sectionId) {
            setActiveSection(step.sectionId);
        }
    }, [visible, stepIndex, setActiveSection]); // eslint-disable-line react-hooks/exhaustive-deps

    if (!visible) return null;

    const step = STEPS[stepIndex];
    const isLast = stepIndex === STEPS.length - 1;

    const handleSkip = () => { markTourSeen(); setVisible(false); };
    const handleNext = () => {
        if (isLast) { markTourSeen(); setVisible(false); return; }
        setStepIndex(i => i + 1);
    };

    return (
        <div
            className="fixed bottom-4 right-4 z-40 w-[360px] max-w-[calc(100vw-2rem)] gch-bg-card border border-[var(--admin-border)] rounded-xl shadow-2xl shadow-black/40 p-4"
            role="dialog"
            aria-label="Editor walkthrough"
        >
            <div className="flex items-center justify-between mb-2">
                <span className="text-[10px] font-bold uppercase tracking-wider text-[var(--admin-accent)]">
                    Tour · {stepIndex + 1} of {STEPS.length}
                </span>
                <div className="flex gap-1">
                    {STEPS.map((_, i) => (
                        <span
                            key={i}
                            className={`h-1 w-4 rounded-full transition-colors ${
                                i <= stepIndex ? 'bg-[var(--admin-accent)]' : 'bg-white/15'
                            }`}
                        />
                    ))}
                </div>
            </div>
            <h3 className="text-sm font-semibold gch-text-primary leading-tight mb-1">
                {step.title}
            </h3>
            <p className="text-xs gch-text-secondary leading-relaxed">
                {step.body}
            </p>
            <div className="mt-4 flex items-center justify-between gap-2">
                <button
                    type="button"
                    onClick={handleSkip}
                    className="text-xs font-medium gch-text-muted hover:gch-text-primary transition-colors"
                >
                    {isLast ? 'Close' : 'Skip tour'}
                </button>
                <button
                    type="button"
                    onClick={handleNext}
                    className="inline-flex items-center justify-center gap-1.5 rounded-lg px-4 py-2 text-xs font-semibold text-white"
                    style={{ background: 'var(--admin-cta-solid)' }}
                >
                    {isLast ? 'Got it' : 'Next →'}
                </button>
            </div>
        </div>
    );
}
